import { CONTRACT_VERSION } from './generated/routes';
/**
 * The typed API client (ADR-008/016).
 *
 * Frontends never hand-write a fetch against a string URL: every call goes through
 * here, so the envelope is unwrapped in one place and an API error becomes a typed
 * exception rather than a shape the caller has to inspect.
 */
export class ApiError extends Error {
    code;
    status;
    details;
    constructor(
    /** Stable machine string — match on this, never on the message. */
    code, message, status, details) {
        super(message);
        this.code = code;
        this.status = status;
        this.details = details;
        this.name = 'ApiError';
    }
    /** True when the customer can fix it by changing their input. */
    get isClientError() {
        return this.status >= 400 && this.status < 500;
    }
}
export class ApiClient {
    options;
    contractVersion = CONTRACT_VERSION;
    constructor(options) {
        this.options = options;
    }
    withLocale(locale) {
        return new ApiClient({ ...this.options, locale });
    }
    /** Unwraps `{ data }` and returns the payload. */
    async request(method, path, options = {}) {
        const response = await this.raw(method, path, options);
        const payload = (await this.parse(response));
        return (payload?.data ?? null);
    }
    /** Unwraps `{ data, meta }` for list endpoints. */
    async requestList(method, path, options = {}) {
        const response = await this.raw(method, path, options);
        const payload = (await this.parse(response));
        return {
            items: payload?.data ?? [],
            meta: payload?.meta ?? { limit: 0, nextCursor: null },
        };
    }
    /**
     * A multipart upload.
     *
     * The body is a `FormData`, and the Content-Type header is deliberately left
     * unset so the browser can add the multipart boundary — setting it by hand
     * produces a body the server cannot parse.
     */
    async requestMultipart(method, path, form) {
        const url = new URL(this.options.baseUrl.replace(/\/$/, '') + path);
        const query = {
            locale: this.options.locale,
            ...this.options.defaultQuery?.(),
        };
        for (const [key, value] of Object.entries(query)) {
            if (value !== undefined && value !== null && value !== '') {
                url.searchParams.set(key, String(value));
            }
        }
        const token = this.options.getAccessToken?.();
        const headers = { Accept: 'application/json' };
        if (token)
            headers.Authorization = `Bearer ${token}`;
        // An upload is limited per caller too, and the caller is the visitor (ADR-043).
        const forwardedFor = this.options.getForwardedFor?.();
        if (forwardedFor)
            headers['X-Forwarded-For'] = forwardedFor;
        let response;
        try {
            response = await fetch(url, { method, headers, body: form });
        }
        catch {
            throw new ApiError('NETWORK_ERROR', 'Could not reach the store. Check your connection and try again.', 0);
        }
        if (!response.ok)
            throw await this.toApiError(response);
        const payload = (await this.parse(response));
        return (payload?.data ?? null);
    }
    /**
     * A file download — a PDF invoice, a CSV export.
     *
     * Fetched rather than linked, because the API authenticates with a bearer
     * token and an `<a href>` carries none: the link would simply 401. The
     * filename comes from the server's Content-Disposition, so the name in the
     * downloads folder is the one the API chose.
     */
    async download(path, options = {}) {
        const response = await this.raw('GET', path, options);
        return {
            blob: await response.blob(),
            filename: filenameFrom(response.headers.get('Content-Disposition')),
        };
    }
    /** For the health probes and other deliberately un-enveloped routes. */
    async requestRaw(method, path, options = {}) {
        const response = await this.raw(method, path, options);
        return (await this.parse(response));
    }
    async raw(method, path, options) {
        const url = new URL(this.options.baseUrl.replace(/\/$/, '') + path);
        // The locale is a query parameter across the API, so the client applies it
        // once rather than every call site remembering.
        const query = {
            locale: this.options.locale,
            ...this.options.defaultQuery?.(),
            ...options.query,
        };
        for (const [key, value] of Object.entries(query)) {
            if (value !== undefined && value !== null && value !== '') {
                url.searchParams.set(key, String(value));
            }
        }
        const token = options.token !== undefined ? options.token : this.options.getAccessToken?.();
        const cartToken = options.cartToken !== undefined ? options.cartToken : this.options.getCartToken?.();
        const savedToken = options.savedToken !== undefined ? options.savedToken : this.options.getSavedToken?.();
        const headers = { Accept: 'application/json' };
        if (options.body !== undefined)
            headers['Content-Type'] = 'application/json';
        if (token)
            headers.Authorization = `Bearer ${token}`;
        if (cartToken)
            headers['X-Cart-Token'] = cartToken;
        if (savedToken)
            headers['X-Saved-Token'] = savedToken;
        if (options.ticketToken)
            headers['X-Ticket-Token'] = options.ticketToken;
        if (options.expressToken)
            headers['X-Express-Cart'] = options.expressToken;
        const forwardedFor = this.options.getForwardedFor?.();
        if (forwardedFor)
            headers['X-Forwarded-For'] = forwardedFor;
        let response;
        try {
            response = await fetch(url, {
                method,
                headers,
                body: options.body === undefined ? undefined : JSON.stringify(options.body),
                signal: options.signal,
                ...this.options.fetchOptions,
                ...options.fetchOptions,
            });
        }
        catch {
            // A network failure is not an API error; give it a code callers can branch on.
            throw new ApiError('NETWORK_ERROR', 'Could not reach the store. Check your connection and try again.', 0, undefined);
        }
        if (!response.ok)
            throw await this.toApiError(response);
        return response;
    }
    async parse(response) {
        if (response.status === 204)
            return null;
        const text = await response.text();
        if (!text)
            return null;
        try {
            return JSON.parse(text);
        }
        catch {
            throw new ApiError('MALFORMED_RESPONSE', 'The store returned an unreadable response.', response.status);
        }
    }
    async toApiError(response) {
        let body = null;
        try {
            body = (await response.json());
        }
        catch {
            // Fall through to the generic message below.
        }
        return new ApiError(body?.error?.code ?? 'REQUEST_FAILED', body?.error?.message ?? 'Something went wrong. Please try again.', response.status, body?.error?.details);
    }
}
/** Pulls `filename="…"` out of a Content-Disposition header. */
function filenameFrom(header) {
    if (!header)
        return null;
    const quoted = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(header);
    return quoted ? decodeURIComponent(quoted[1]) : null;
}
//# sourceMappingURL=client.js.map